import { getAllPickups } from "@/api/pickups/getPickup";
import FormTitle from "@/components/composite/Headers/FormTitle";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useUserStore } from "@/core/UserStore";
import { useEffect, useState } from "react";
import { PickupData } from "@shared/PickupTypes";
import { useFormStore } from "@/core/ShipmentFormStore";
import _, { pick } from "lodash";
import dayjs from "dayjs";
import { Button } from "@/components/ui/button";
import { get, useFormContext } from "react-hook-form";
import { Shipment } from "@shared/ShipmentTypes";

const PickupLocation = {
    frontDoor: "Front door",
    backEntrance: "Back entrance",
    mainLobby: "Main lobby",
    rearEntrance: "Rear entrance",
    sideEntrance: "Side entrance",
    receptionDesk: "Reception desk",
    mailroom: "Mailroom",
    conferenceRoom: "Conference room",
    employeeEntrance: "Employee entrance",
    loadingDock: "Loading dock",
    securityDesk: "Security desk",
    visitorCenter: "Visitor center",
    stairwell: "Stairwell",
    elevatorLobby: "Elevator lobby",
    warehouseEntrance: "Warehouse entrance",
    garageEntrance: "Garage entrance"
};

interface UseExistingPickup {
    shipment: Shipment;
    selectedPickup: PickupData | undefined;
    setSelectedPickup: React.Dispatch<React.SetStateAction<PickupData | undefined>>;
}
export default function UseExistingPickup({ shipment, selectedPickup, setSelectedPickup }: UseExistingPickup) {
    const userData = useUserStore((state) => state.userData);

    const shipperAddress = shipment.shipper;
    const carrier = shipment.serviceDetails?.carrier;

    const [pickupList, setPickupList] = useState<(PickupData | undefined)[] | null>([]);
    useEffect(() => {
        if (userData) {
            getAllPickups(userData.email)
                .then((response) => {
                    const pickups = response.filter((pickup) => {
                        if (_.isEqual(pickup.shipper, shipperAddress)) { 
                            if (pickup.status !== "Cancelled" && pickup.carrier === carrier && dayjs(pickup.pickupDetails.closeTimeStamp?.isoTimeStamp).isAfter(dayjs())) {
                                return pickup;
                            }
                        }
                    });
                    if (pickups.length <= 0) {
                        throw "No Pickups available";
                    }
                    setPickupList(pickups);
                })
                .catch((e) => {
                    console.error(e);
                    setPickupList(null);
                });
        }
    }, [userData]);

    return (
        <Card className="">
            <CardHeader className="p-4">
                <FormTitle>Schedule Pickup</FormTitle>
            </CardHeader>
            <CardContent className="grid h-96 grid-cols-2 gap-6 overflow-scroll p-4">
                {pickupList ? (
                    pickupList.length > 0 ? (
                        pickupList.map((pickup) => {
                            if (pickup) {
                                return <PickupCard pickupData={pickup} selectedPickup={selectedPickup} setSelectedPickup={setSelectedPickup} key={pickup.id} />;
                            }
                        })
                    ) : (
                        <Skeleton className="col-span-2 h-10 w-full" />
                    )
                ) : (
                    "There is no pickups booked"
                )}
            </CardContent>
        </Card>
    );
}

interface PickupCard {
    pickupData: PickupData;
    selectedPickup?: PickupData;
    setSelectedPickup: React.Dispatch<React.SetStateAction<PickupData | undefined>>;
}
const PickupCard = ({ pickupData, selectedPickup, setSelectedPickup }: PickupCard) => {
    const handleClick = () => {
        setSelectedPickup(pickupData);
    };

    return (
        <Card className="h-fit">
            <CardHeader>
                <CardTitle className="text-sm">{pickupData.id}</CardTitle>
                <CardDescription>
                    {dayjs(pickupData.pickupDetails.readyTimeStamp?.isoTimeStamp).format("MMM D, YYYY")} from {dayjs(pickupData.pickupDetails.readyTimeStamp?.isoTimeStamp).format("h:mmA")} to{" "}
                    {dayjs(pickupData.pickupDetails.closeTimeStamp?.isoTimeStamp).format("h:mmA")}
                </CardDescription>
            </CardHeader>
            <CardContent>
                <Button type="button" variant={selectedPickup?.id === pickupData.id ? "default" : "secondary"} className="w-full" onClick={handleClick}>
                    {selectedPickup?.id === pickupData.id ? "Pickup selected" : "Use this pickup"}{" "}
                </Button>
            </CardContent>
        </Card>
    );
};
